import { Fragment, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  line, CHIPS_PER_DAY, COMPACT_DOTS, Color, fill, Size, WEEKDAYS, anchorCard, blockDetail, blockPlacement, calendarCompact, chipKind, chipLabel, clockOf, eventsOnDay, hourLabel, layOutMonth, layOutWeek, monthName, shiftMonth, shiftWeek, shortName, t,
  teamControlLabel, toggleOverlay, travelLabel, viewOnly, weekStart,
  type AnchorBox, type Attending, type GridEvent, type GridWeek, type TeamMember, type TeamSelection, type WeekBlock,
} from '@bms2/shared';
import { Button, Checkbox } from '../../components/controls';
import { AvatarRow } from '../../components/composites/AttendeeAvatar';
import { Icon } from '../../components/Icon';
import styles from './CalendarGrid.module.css';

/**
 * The Calendar's controls and its two grids (piece 12, the Calendar page).
 * 🔨 A multi-day event is one continuous bar across every cell it covers,
 * labelled once and carrying a continuation arrow, wrapped one segment per
 * week — never a chip repeated per day. A day cell holds two chips and counts
 * the rest as overflow. Clicking a chip opens the Event Booking card, Expanded,
 * anchored to that day; right-clicking an empty cell opens the Add/Edit Event
 * modal pre-filled with that date. The week view draws hourly slots, and the
 * Travel Buffer as its own block before the event's.
 *
 * 🔨 The month scroller, Today and the Month/Week toggle stand in the page's
 * Title Banner, spread across it; Team, + Add Event and Show cancelled are one
 * row directly beneath, and the grid fills the content region's height
 * (decision 0329). 💰 The toolbar stood as its own bar above the grid, and the
 * grid ended under its last row with the region's remaining height unused
 * (product owner, 2026-09-12).
 *
 * 🔨 Under the small breakpoint in width, or under the compact height, the
 * grid is compact (decisions 0266 and 0329): a day cell draws a dot per event
 * and a tapped day lists its events beneath the grid, the week is the seven
 * days as the same lists, and the card opens inline under its row. 💰 At a
 * phone's width a chip was fifty pixels of ellipsis, the overflow count
 * wrapped onto the next row, and the anchored card ran off the edge of the
 * screen (found while building, 2026-09-09).
 *
 * 💰 The calendar shipped as a list of condensed rows, which the piece never
 * described: the toolbar, the grid, the chips and the legend were all specified
 * and none of them was built (found while building, 2026-09-09). 💰 Its second
 * cut hid the grid behind the empty state, put the legend under the grid and
 * opened the card below it: three more departures from figures 65 and 68, each
 * pointed out by the product owner the same day.
 */
export interface OpenedEvent {
  /** The grid event's own key, which a series occurrence carries distinctly from its siblings. */
  eventKey: string;
  /** The day cell the card anchors to. */
  date: string;
}

export interface TeamControl {
  viewerPersonId: string;
  members: readonly TeamMember[];
  selection: TeamSelection;
  onSelectionChange: (selection: TeamSelection) => void;
}

export interface CalendarViewControlsProps {
  /** The first of the month on show, as YYYY-MM-DD. */
  monthStart: string;
  /** The Sunday of the week on show, for the week scale. */
  weekOf: string;
  /** Today in the viewer's zone, which the Today button returns to. */
  today: string;
  scale: 'month' | 'week';
  onMonthChange: (monthStart: string) => void;
  onWeekChange: (weekOf: string) => void;
  onScaleChange: (scale: 'month' | 'week') => void;
}

/** 🔨 The week on show as words, such as `Oct 18 – 24, 2026` or `Oct 25 – Nov 1, 2026` (T1128): the Sunday to the Saturday. */
function weekHeading(weekOf: string): string {
  const first = new Date(`${weekOf}T00:00:00Z`);
  const last = new Date(first.getTime() + 6 * 86_400_000).toISOString().slice(0, 10);
  const startMonth = monthName(weekOf);
  const endMonth = monthName(`${last.slice(0, 7)}-01`);
  const endText = startMonth === endMonth ? String(Number(last.slice(8))) : `${endMonth} ${Number(last.slice(8))}`;
  const startYear = weekOf.slice(0, 4);
  const endYear = last.slice(0, 4);
  return `${startMonth} ${Number(weekOf.slice(8))}${startYear === endYear ? '' : `, ${startYear}`} \u2013 ${endText}, ${endYear}`;
}

/**
 * The banner's controls (decision 0329): Prev, the month and year as a
 * heading, Next; Today; the Month/Week toggle. The three groups spread across
 * the banner's width between the title and its right edge.
 */
export function CalendarViewControls({ monthStart, weekOf, today, scale, onMonthChange, onWeekChange, onScaleChange }: CalendarViewControlsProps) {
  const stepBack = () => (scale === 'week' ? onWeekChange(shiftWeek(weekOf, -1)) : onMonthChange(shiftMonth(monthStart, -1)));
  const stepForward = () => (scale === 'week' ? onWeekChange(shiftWeek(weekOf, 1)) : onMonthChange(shiftMonth(monthStart, 1)));
  return (
    <div className={styles.viewControls} role="group" aria-label={t('calendar.viewControls')} data-calendar="view-controls">
      <div className={styles.strip}>
        <button type="button" className={styles.step} onClick={stepBack} aria-label={t(scale === 'week' ? 'calendar.previousWeek' : 'calendar.previousMonth')} data-calendar-action="previous">
          <Icon meaning="previous" size="nav" />
        </button>
        {/* The month and the year at the top, in the banner with the scale and Today (decisions 0278 and 0329). */}
        <h2 className={styles.heading} data-calendar-heading={scale === 'week' ? weekOf : monthStart.slice(0, 7)}>
          {scale === 'week' ? weekHeading(weekOf) : line(monthName(monthStart, true), monthStart.slice(0, 4))}
        </h2>
        <button type="button" className={styles.step} onClick={stepForward} aria-label={t(scale === 'week' ? 'calendar.nextWeek' : 'calendar.nextMonth')} data-calendar-action="next">
          <Icon meaning="next" size="nav" />
        </button>
      </div>

      <button
        type="button"
        className={styles.today}
        onClick={() => {
          onMonthChange(`${today.slice(0, 7)}-01`);
          onWeekChange(weekStart(today));
        }}
        data-calendar-action="today"
      >
        {t('calendar.today')}
      </button>

      <div className={styles.scales}>
        {(['month', 'week'] as const).map((option) => (
          <button key={option} type="button" className={styles.scale} aria-pressed={scale === option} onClick={() => onScaleChange(option)} data-calendar-scale-option={option}>
            {t(option === 'month' ? 'calendar.month' : 'calendar.week')}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface CalendarActionsProps {
  team: TeamControl | null;
  showCancelled: boolean;
  onShowCancelledChange: (shown: boolean) => void;
  onAddEvent: () => void;
}

/**
 * The row directly under the banner (decision 0329): the Team dropdown on the
 * left, + Add Event and Show cancelled on the right, under the Month/Week
 * toggle above them.
 */
export function CalendarActions({ team, showCancelled, onShowCancelledChange, onAddEvent }: CalendarActionsProps) {
  return (
    <div className={styles.actions} data-calendar="actions">
      {/* 🔨 The selector is offered whoever the viewer is (decision 0602): somebody with nobody under them
          gets it holding My calendar alone and a line saying why, rather than no control and no explanation. */}
      {team ? <TeamDropdown team={team} /> : null}
      <div className={styles.actionsRight}>
        <Button size={Size.Medium} color={Color.Navy} text={[t('calendar.addEvent')]} actions={[onAddEvent]} />
        {/* 🔨 A cancelled meeting is hidden until this is on (decision 0262). */}
        <Checkbox label={t('event.showCancelled')} checked={showCancelled} onChange={onShowCancelledChange} size={Size.Small} />
      </div>
    </div>
  );
}

export interface CalendarGridProps {
  /** The first of the month on show, as YYYY-MM-DD. */
  monthStart: string;
  /** The Sunday of the week on show, for the week scale. */
  weekOf: string;
  events: readonly GridEvent[];
  /** The viewer's own zone; the grid is laid out in it, never in stored UTC. */
  timeZone: string;
  /** Today in that zone, so the marker does not follow the machine's clock. */
  today: string;
  scale: 'month' | 'week';
  /** The colour each owner's events carry in Overlay, by person; absent outside Overlay. */
  ownerColors: ReadonlyMap<string, string>;
  /** The team selection, which the people legend names in Overlay. */
  team: TeamControl | null;
  opened: OpenedEvent | null;
  /** The Expanded card for the opened event, which the page builds because it holds the rows. */
  renderCard: (opened: OpenedEvent) => ReactNode;
  onOpenEvent: (opened: OpenedEvent | null) => void;
  /** Right-clicking an empty cell starts an event on that day (piece 12). */
  onAddOnDay: (date: string) => void;
  /** A measured width for the suite and the harness; the grid measures itself otherwise. */
  widthOverride?: number;
  /** A measured content-region height for the suite and the harness; the grid measures its scroll region otherwise. */
  heightOverride?: number;
  /** 🔒 The holidays on show, each calendar day to its name, drawn as a background band (piece 15; decision 1720). */
  holidays?: ReadonlyMap<string, string>;
}

const NO_HOLIDAYS: ReadonlyMap<string, string> = new Map();

/**
 * 🔨 One legend entry per colour family, and one family per event type
 * (decision 0597): the five types of piece 2 draw in five colours, and the
 * neutral pair is what a type nothing names falls to. 💰 Time off and a work
 * order's blocked time both drew neutral grey, so the two kinds that are
 * usually the multi-day ones were the two the grid would not colour (product
 * owner, 2026-09-20).
 */
const LEGEND = [
  { kind: 'inPerson', label: 'event.legend.inPerson' },
  { kind: 'online', label: 'event.legend.online' },
  { kind: 'call', label: 'event.legend.call' },
  { kind: 'pto', label: 'event.legend.pto' },
  { kind: 'workOrder', label: 'event.legend.workOrder' },
  { kind: 'none', label: 'event.legend.none' },
] as const;

/** The scrolling region the grid fills; its height, not the grid's own, is what the compact rule reads. */
const CONTENT_REGION = '[data-shell="content"]';

export function CalendarGrid({
  monthStart, weekOf, events, timeZone, today, scale, ownerColors, team, opened, renderCard,
  onOpenEvent, onAddOnDay, widthOverride, heightOverride, holidays = NO_HOLIDAYS,
}: CalendarGridProps) {
  const overlaying = team !== null && team.selection.overlay.length > 0 && !team.selection.viewOnly;
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{ width: number | null; height: number | null }>({ width: null, height: null });
  const width = widthOverride ?? measured.width;
  const height = heightOverride ?? measured.height;
  useLayoutEffect(() => {
    if (widthOverride !== undefined && heightOverride !== undefined) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const region = el.closest<HTMLElement>(CONTENT_REGION);
    const read = () => setMeasured({ width: el.clientWidth || null, height: region?.clientHeight || null });
    const observer = new ResizeObserver(read);
    observer.observe(el);
    if (region) observer.observe(region);
    return () => observer.disconnect();
  }, [widthOverride, heightOverride]);
  const compact = calendarCompact(width, height);
  const chipStyle = (event: GridEvent): CSSProperties | undefined => {
    const color = event.ownerPersonId ? ownerColors.get(event.ownerPersonId) : undefined;
    return color ? ({ '--owner-color': color } as CSSProperties) : undefined;
  };

  return (
    <div ref={ref} className={compact ? `${styles.calendar} ${styles.compact}` : styles.calendar} data-calendar="grid" data-calendar-scale={scale} data-calendar-compact={compact ? 'true' : 'false'}>
      {/* 🔨 The legend stands above the grid, always visible (figure 65); in Overlay it also names each person and their colour (figure 67). */}
      <div className={styles.toolbarRow} data-calendar="legend">
        <span className={styles.legendTitle}>{t('calendar.legendTitle')}</span>
        <span className={styles.legend}>
          {LEGEND.map((entry) => (
            <span key={entry.kind} className={styles.legendItem}>
              <span className={styles.swatch} data-legend={entry.kind} />
              {t(entry.label)}
            </span>
          ))}
        </span>
      </div>
      {overlaying && team ? (
        <div className={styles.toolbarRow} data-calendar="people-legend">
          <span className={styles.legend}>
            <span className={styles.legendItem} data-person="true">
              {t('calendar.myCalendar')}
              <span className={styles.swatch} />
            </span>
            {team.selection.overlay.map((personId) => {
              const member = team.members.find((m) => m.personId === personId);
              if (!member) return null;
              return (
                <span key={personId} className={styles.legendItem} data-person="true" style={{ color: ownerColors.get(personId) }}>
                  {shortName(member.name)}
                  <span className={styles.swatch} style={{ '--swatch-color': ownerColors.get(personId) } as CSSProperties} />
                </span>
              );
            })}
          </span>
        </div>
      ) : null}

      {compact ? (
        <CompactCalendar
          scale={scale}
          monthStart={monthStart}
          weekOf={weekOf}
          events={events}
          timeZone={timeZone}
          today={today}
          opened={opened}
          renderCard={renderCard}
          chipStyle={chipStyle}
          overlaying={overlaying}
          onOpenEvent={onOpenEvent}
          onAddOnDay={onAddOnDay}
          holidays={holidays}
        />
      ) : scale === 'month' ? (
        <MonthGrid
          monthStart={monthStart}
          events={events}
          timeZone={timeZone}
          today={today}
          opened={opened}
          renderCard={renderCard}
          chipStyle={chipStyle}
          overlaying={overlaying}
          onOpenEvent={onOpenEvent}
          onAddOnDay={onAddOnDay}
          holidays={holidays}
        />
      ) : (
        <WeekGrid
          weekOf={weekOf}
          events={events}
          timeZone={timeZone}
          today={today}
          opened={opened}
          renderCard={renderCard}
          chipStyle={chipStyle}
          overlaying={overlaying}
          onOpenEvent={onOpenEvent}
          onAddOnDay={onAddOnDay}
          holidays={holidays}
        />
      )}
    </div>
  );
}

/** A day's heading on the phone: "Thursday, September 10". */
function dayHeading(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)));
}

/** One event as a full-width row on the phone: the time in the viewer's zone, the subject, the type's colour on the left bar. */
function DayRow({ event, date, timeZone, chipStyle, overlaying, onOpenEvent }: { event: GridEvent; date: string; timeZone: string; chipStyle: (event: GridEvent) => CSSProperties | undefined; overlaying: boolean; onOpenEvent: (opened: OpenedEvent | null) => void }) {
  const ends = event.endsAt ? clockOf(event.endsAt, timeZone) : null;
  return (
    <button
      type="button"
      className={styles.dayRow}
      style={chipStyle(event)}
      data-kind={chipKind(event.eventType)}
      data-owned={overlaying ? 'true' : 'false'}
      data-cancelled={event.cancelledAt ? 'true' : 'false'}
      data-calendar-chip={event.id}
      onClick={() => onOpenEvent({ eventKey: event.id, date })}
    >
      <span className={styles.dayRowTime}>{ends ? `${clockOf(event.startsAt, timeZone)} – ${ends}` : clockOf(event.startsAt, timeZone)}</span>
      <span className={styles.dayRowTitle}>{event.ownerLabel ? `${event.ownerLabel}: ${event.subject}` : event.subject}</span>
    </button>
  );
}

/**
 * The calendar under the small breakpoint (decision 0266). Month: the six
 * weeks as compact cells, a dot per event, and the tapped day's events listed
 * beneath. Week: the seven days as the same lists, one after another. The
 * card opens inline under its row in both.
 */
function CompactCalendar({ scale, monthStart, weekOf, events, timeZone, today, opened, renderCard, chipStyle, overlaying, onOpenEvent, onAddOnDay, holidays }: GridBodyProps & { scale: 'month' | 'week'; monthStart: string; weekOf: string }) {
  /**
   * 🔨 The pick carries the month it was made in, so a day the grid draws from
   * the month before or after is selected like any other and the pick still
   * resets when the reader pages to another month. 💰 The pick was kept only
   * while it fell inside the month on show, so nine of the grid's trailing
   * cells changed nothing at all when they were pressed, and the every-button
   * walk reported each of them (found while building, 2026-09-20).
   */
  const [picked, setPicked] = useState<{ month: string; date: string } | null>(null);
  const weeks = layOutMonth(monthStart, events, timeZone, today);
  const inMonth = today.slice(0, 7) === monthStart.slice(0, 7) ? today : monthStart;
  const selected = picked && picked.month === monthStart ? picked.date : inMonth;

  const dayList = (date: string, heading: string) => {
    const rows = eventsOnDay(events, date, timeZone);
    return (
      <section key={date} className={styles.dayPanel} data-calendar-day-list={date} data-holiday={holidays.has(date) ? 'true' : 'false'}>
        <div className={styles.dayPanelHead}>
          <span className={styles.dayPanelTitle}>
            {heading}
            <HolidayName date={date} name={holidays.get(date)} />
          </span>
          <Button size={Size.Small} color={Color.Inactive} text={[t('calendar.addOnDay')]} actions={[() => onAddOnDay(date)]} />
        </div>
        {rows.length === 0 ? <p className={styles.hint}>{t('calendar.noEventsOn')}</p> : null}
        {/* 🔨 The open card stands in its row's place, by itself: the Expanded card is its own frame and carries the type's colour on its own bar, so no wrapper is drawn around it (decision 0266; product owner 2026-09-10, "the inline wrapper is not needed since the card is the wrapper"). */}
        {rows.map((event) =>
          opened && opened.eventKey === event.id && opened.date === date ? (
            <Fragment key={event.id}>{renderCard(opened)}</Fragment>
          ) : (
            <DayRow key={event.id} event={event} date={date} timeZone={timeZone} chipStyle={chipStyle} overlaying={overlaying} onOpenEvent={onOpenEvent} />
          ),
        )}
      </section>
    );
  };

  if (scale === 'week') {
    const week = layOutWeek(weekOf, events, timeZone, today);
    return <div data-calendar="week" data-calendar-week-list="true">{week.days.map((day) => dayList(day.date, dayHeading(day.date)))}</div>;
  }

  return (
    <>
      <div className={`${styles.grid} ${styles.compactGrid}`} data-calendar="month">
        <div className={styles.weekdays}>
          {WEEKDAYS.map((day) => (
            <span key={day} className={styles.weekday}>{day.slice(0, 1)}</span>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week.days[0]!.date} className={styles.week} data-calendar-week={week.days[0]!.date}>
            {week.days.map((day) => {
              const rows = eventsOnDay(events, day.date, timeZone);
              return (
                <button
                  key={day.date}
                  type="button"
                  className={`${styles.cell} ${styles.compactCell}`}
                  aria-pressed={day.date === selected}
                  data-in-month={day.inMonth ? 'true' : 'false'}
                  data-today={day.isToday ? 'true' : 'false'}
                  data-calendar-day={day.date}
                  data-holiday={holidays.has(day.date) ? 'true' : 'false'}
                  title={holidays.get(day.date)}
                  data-selected={day.date === selected ? 'true' : 'false'}
                  onClick={() => setPicked({ month: monthStart, date: day.date })}
                >
                  <span className={styles.dayNumber} data-today={day.isToday ? 'true' : 'false'}>{day.dayOfMonth}</span>
                  <span className={styles.dots} aria-label={rows.length === 0 ? undefined : String(rows.length)}>
                    {rows.slice(0, COMPACT_DOTS).map((event) => (
                      <span key={event.id} className={styles.dot} data-kind={chipKind(event.eventType)} />
                    ))}
                    {rows.length > COMPACT_DOTS ? <span className={styles.dotMore}>{fill(t('calendar.moreDots'), { n: String(rows.length - COMPACT_DOTS) })}</span> : null}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {dayList(selected, dayHeading(selected))}
    </>
  );
}

/** The Team dropdown (figure 66): My Calendar always checked, then the direct reports, each with Overlay and View Only. */
function TeamDropdown({ team }: { team: TeamControl }) {
  const [open, setOpen] = useState(false);
  const words = { overlay: t('calendar.overlay'), myCalendar: t('calendar.myCalendar'), viewing: t('calendar.viewing'), calendarOf: t('calendar.calendarOf'), selected: t('calendar.selected') };
  const label = `${t('calendar.team')}: ${teamControlLabel(team.selection, team.members, words)}`;
  return (
    <div className={styles.team} data-calendar="team">
      <button type="button" className={styles.teamButton} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((was) => !was)} data-calendar-team="toggle">
        {label}
      </button>
      {open ? (
        <div className={styles.teamPanel} role="menu" data-calendar-team="panel">
          <div className={styles.teamRow} data-mine="true">
            <span className={styles.teamText}>
              {/* 🔨 My Calendar is always shown and cannot be unchecked. */}
              <Checkbox label={t('calendar.myCalendar')} checked onChange={() => undefined} disabled size={Size.Small} />
              <span className={styles.teamCaption}>{t('calendar.alwaysShown')}</span>
            </span>
            {team.selection.viewOnly ? (
              <button type="button" className={styles.viewOnly} onClick={() => team.onSelectionChange(viewOnly(team.selection, null))} data-calendar-team="mine">
                {t('calendar.backToMine')}
              </button>
            ) : null}
          </div>
          <p className={styles.teamHeading}>{t('calendar.directReports')}</p>
          {/*
            🔒 Why nothing else is on offer, said on the control itself (decision 0602). 💰 The walk goes
            downward only, so a person with no direct reports is offered nobody — their own manager included —
            and until now the dropdown simply did not appear (flagged to the product owner, 2026-09-17).
          */}
          {team.members.length === 0 ? <p className={styles.teamCaption} data-calendar-team="no-reports">{t('calendar.noReports')}</p> : null}
          {team.members.map((member) => {
            const shown = team.selection.overlay.includes(member.personId) && !team.selection.viewOnly;
            const viewed = team.selection.viewOnly === member.personId;
            const color = `var(--${member.avatarColor})`;
            return (
              <div key={member.personId} className={styles.teamRow} data-calendar-team-member={member.personId}>
                <span className={styles.teamText}>
                  <Checkbox label={member.name} checked={shown} onChange={() => team.onSelectionChange(toggleOverlay(team.selection, member.personId))} size={Size.Small} />
                  <span className={styles.teamCaption} data-shown={shown ? 'true' : 'false'} style={{ '--person-color': color } as CSSProperties}>
                    {shown ? t('calendar.overlay') : viewed ? t('calendar.viewing') : t('calendar.notShown')}
                  </span>
                </span>
                <button type="button" className={styles.viewOnly} aria-pressed={viewed} onClick={() => team.onSelectionChange(viewOnly(team.selection, viewed ? null : member.personId))}>
                  {t('calendar.viewOnly')}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface GridBodyProps {
  events: readonly GridEvent[];
  timeZone: string;
  today: string;
  opened: OpenedEvent | null;
  renderCard: (opened: OpenedEvent) => ReactNode;
  chipStyle: (event: GridEvent) => CSSProperties | undefined;
  overlaying: boolean;
  onOpenEvent: (opened: OpenedEvent | null) => void;
  onAddOnDay: (date: string) => void;
  holidays: ReadonlyMap<string, string>;
}

/**
 * 🔒 A holiday's band: the day's ground tinted and the holiday's name beside the
 * day number (piece 15; decision 1720). It is drawn behind the events, never as
 * one of them, so it opens no card and takes none of the day's two chip rows.
 */
function HolidayName({ date, name }: { date: string; name: string | undefined }) {
  if (!name) return null;
  return (
    <span className={styles.holidayName} data-calendar-holiday={date} title={name}>
      {name}
    </span>
  );
}

/**
 * The chip the card was opened from, which is the thing it anchors to. One
 * event can be drawn twice in a month — a multi-day bar wraps a segment onto
 * each week it crosses — so the day the card names picks between them.
 */
function openedChip(bounds: HTMLElement, opened: OpenedEvent): HTMLElement | null {
  const drawn = [...bounds.querySelectorAll<HTMLElement>('[data-calendar-chip], [data-calendar-band]')]
    .filter((element) => (element.dataset['calendarChip'] ?? element.dataset['calendarBand']) === opened.eventKey);
  if (drawn.length <= 1) return drawn[0] ?? null;
  const day = bounds.querySelector<HTMLElement>(`[data-calendar-day="${opened.date}"]`);
  const week = day?.closest<HTMLElement>('[data-calendar-week]') ?? null;
  return drawn.find((element) => day?.contains(element)) ?? drawn.find((element) => week?.contains(element)) ?? drawn[0] ?? null;
}

/**
 * A rectangle in the grid's own coordinates: its padding box, and its scroll
 * carried with it. `clientLeft` and `clientTop` are the frame's border widths,
 * which is what the padding box is inset by.
 */
function boxIn(bounds: HTMLElement, rect: DOMRect): AnchorBox {
  const outer = bounds.getBoundingClientRect();
  return {
    left: rect.left - outer.left - bounds.clientLeft + bounds.scrollLeft,
    top: rect.top - outer.top - bounds.clientTop + bounds.scrollTop,
    width: rect.width,
    height: rect.height,
  };
}

/** The part of the grid on show, in those same coordinates; a week is taller than its frame and scrolls. */
function shownPartOf(bounds: HTMLElement): AnchorBox {
  return { left: bounds.scrollLeft, top: bounds.scrollTop, width: bounds.clientWidth, height: bounds.clientHeight };
}

/**
 * The Expanded card, anchored to the chip it was opened from: beside it, level
 * with it, flipped to the other side where the grid's edge is closer than the
 * card is wide, and never with an edge outside what is on show.
 *
 * 💰 The card was placed from the day's column number alone, so it opened at
 * the top of the column whatever hour the block sat at, and the week draws
 * midnight to midnight now (product owner, 2026-09-14). 💰 Before that it was
 * clamped to the column's width and stood squeezed inside it (product owner,
 * 2026-09-10).
 */
function AnchoredCard({ opened, layout, renderCard }: { opened: OpenedEvent; layout: 'month' | 'week'; renderCard: (opened: OpenedEvent) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const { eventKey, date } = opened;
  useLayoutEffect(() => {
    const card = ref.current;
    // 🔨 The frame is found from the card's own node, never from a ref the grid
    // holds: a child's layout effect runs before its parent's ref is attached,
    // so on the open a ref handed down is still null.
    const grid = card?.closest<HTMLElement>('[data-calendar="month"], [data-calendar="week"]') ?? null;
    if (!card || !grid) return;
    const chip = openedChip(grid, { eventKey, date });
    const from = chip ? boxIn(grid, chip.getBoundingClientRect()) : { left: 0, top: 0, width: 0, height: 0 };
    setAt(anchorCard(from, shownPartOf(grid), { width: card.offsetWidth, height: card.offsetHeight }));
  }, [eventKey, date]);
  const placed: CSSProperties = at ? { left: `${at.left}px`, top: `${at.top}px` } : { visibility: 'hidden' };
  return (
    <div ref={ref} className={styles.card} style={placed} data-layout={layout} data-calendar="card" data-calendar-card-for={opened.date} onContextMenu={(event) => event.stopPropagation()}>
      {renderCard(opened)}
    </div>
  );
}

function MonthGrid({ monthStart, events, timeZone, today, opened, renderCard, chipStyle, overlaying, onOpenEvent, onAddOnDay, holidays }: GridBodyProps & { monthStart: string }) {
  const weeks = layOutMonth(monthStart, events, timeZone, today);
  // 🔨 The card is a child of the grid, not of one week row, so it anchors to the chip wherever in the month that chip is drawn.
  return (
    <div className={styles.grid} data-calendar="month">
      <div className={styles.weekdays}>
        {WEEKDAYS.map((day) => (
          <span key={day} className={styles.weekday}>{day}</span>
        ))}
      </div>
      {weeks.map((week) => (
        <Week
          key={week.days[0]!.date}
          week={week}
          timeZone={timeZone}
          chipStyle={chipStyle}
          overlaying={overlaying}
          onOpenEvent={onOpenEvent}
          onAddOnDay={onAddOnDay}
          holidays={holidays}
        />
      ))}
      {opened ? <AnchoredCard opened={opened} layout="month" renderCard={renderCard} /> : null}
    </div>
  );
}

function Week({ week, timeZone, chipStyle, overlaying, onOpenEvent, onAddOnDay, holidays }: Omit<GridBodyProps, 'events' | 'today' | 'opened' | 'renderCard'> & { week: GridWeek }) {
  return (
    <div className={styles.week} data-calendar-week={week.days[0]!.date}>
      {week.days.map((day) => (
        <div
          key={day.date}
          className={styles.cell}
          data-in-month={day.inMonth ? 'true' : 'false'}
          /* 🔒 The cell carries the mark, not only its number (decision 0453). */
          data-today={day.isToday ? 'true' : 'false'}
          data-calendar-day={day.date}
          data-holiday={holidays.has(day.date) ? 'true' : 'false'}
          onContextMenu={(event) => {
            event.preventDefault();
            onAddOnDay(day.date);
          }}
          onDoubleClick={() => onAddOnDay(day.date)}
        >
          <span className={styles.cellHead}>
            <span className={styles.dayNumber} data-today={day.isToday ? 'true' : 'false'}>{day.dayOfMonth}</span>
            <HolidayName date={day.date} name={holidays.get(day.date)} />
          </span>
        </div>
      ))}

      <div className={styles.bars}>
        {week.segments.map((segment) => (
          <button
            key={`${segment.event.id}-${segment.column}`}
            type="button"
            className={styles.chip}
            style={{ gridColumn: `${segment.column + 1} / span ${segment.span}`, gridRow: segment.row + 1, ...chipStyle(segment.event) }}
            data-kind={chipKind(segment.event.eventType)}
            data-owned={overlaying ? 'true' : 'false'}
            data-cancelled={segment.event.cancelledAt ? 'true' : 'false'}
            data-calendar-chip={segment.event.id}
            onClick={() => onOpenEvent({ eventKey: segment.event.id, date: week.days[segment.column]!.date })}
            title={segment.event.subject}
          >
            {chipLabel(segment.event, timeZone, segment.continuesBefore)}
            {segment.continuesAfter ? ' →' : ''}
          </button>
        ))}
        {Object.entries(week.overflow).map(([date, count]) => {
          const column = week.days.findIndex((day) => day.date === date);
          return (
            <button
              key={date}
              type="button"
              className={styles.more}
              style={{ gridColumn: `${column + 1} / span 1`, gridRow: CHIPS_PER_DAY + 1 }}
              onClick={() => onAddOnDay(date)}
              data-calendar-more={date}
            >
              {fill(t('calendar.more'), { n: String(count) })}
            </button>
          );
        })}
      </div>

    </div>
  );
}

/** A week column's heading: the weekday over the day of the month, "THU 13". */
function columnHeading(column: number, dayOfMonth: number): string {
  return `${WEEKDAYS[column]} ${dayOfMonth}`;
}

function WeekGrid({ weekOf, events, timeZone, today, opened, renderCard, chipStyle, overlaying, onOpenEvent, onAddOnDay, holidays }: GridBodyProps & { weekOf: string }) {
  const week = layOutWeek(weekOf, events, timeZone, today);
  const hours: number[] = [];
  for (let hour = week.fromHour; hour < week.toHour; hour += 1) hours.push(hour);
  const percent = (fraction: number) => `${fraction * 100}%`;
  // 🔨 The card is a child of the week frame, so it anchors to the block's own hour and stays inside the hours on show.
  return (
    <div className={styles.weekGrid} data-calendar="week" data-has-bands={week.bands.length > 0 ? 'true' : 'false'} style={{ '--calendar-week-hours': hours.length } as CSSProperties}>
      <span className={styles.weekHead} />
      {week.days.map((day, column) => (
        <span key={day.date} className={styles.weekHead} data-today={day.isToday ? 'true' : 'false'} data-holiday={holidays.has(day.date) ? 'true' : 'false'}>
          {columnHeading(column, day.dayOfMonth)}
          <HolidayName date={day.date} name={holidays.get(day.date)} />
        </span>
      ))}
      {week.bands.length > 0 ? (
        <>
          <span className={styles.bandGutter} />
          {week.days.map((day, column) => (
            <div key={day.date} className={styles.bandCell} data-calendar-bands={day.date}>
              {week.bands
                .filter((band) => band.column === column)
                .map((band) => (
                  <button
                    key={band.event.id}
                    type="button"
                    className={styles.band}
                    style={chipStyle(band.event)}
                    data-kind={chipKind(band.event.eventType)}
                    data-owned={overlaying ? 'true' : 'false'}
                    /* 🔨 The band takes the chip's own marks: the type's colour, the owner's bar in Overlay and the strike of a cancelled meeting (product owner, 2026-09-20). */
                    data-cancelled={band.event.cancelledAt ? 'true' : 'false'}
                    data-calendar-band={band.event.id}
                    data-continues-before={band.continuesBefore ? 'true' : 'false'}
                    data-continues-after={band.continuesAfter ? 'true' : 'false'}
                    onClick={() => onOpenEvent({ eventKey: band.event.id, date: day.date })}
                    title={band.event.subject}
                  >
                    {band.continuesBefore ? '' : band.event.subject}
                    {band.continuesAfter ? ' →' : ''}
                  </button>
                ))}
            </div>
          ))}
        </>
      ) : null}
      <div className={styles.hours}>
        {hours.map((hour) => (
          <span key={hour} className={styles.hour}>{hourLabel(hour)}</span>
        ))}
      </div>
      {week.days.map((day, column) => (
        <div
          key={day.date}
          className={styles.column}
          data-today={day.isToday ? 'true' : 'false'}
          data-calendar-day={day.date}
          data-holiday={holidays.has(day.date) ? 'true' : 'false'}
          onContextMenu={(event) => {
            event.preventDefault();
            onAddOnDay(day.date);
          }}
          onDoubleClick={() => onAddOnDay(day.date)}
        >
          {week.blocks
            .filter((block) => block.column === column)
            .map((block) => {
              const placed = blockPlacement(block, week.fromHour, week.toHour);
              return <Block key={`${block.event.id}-${block.kind}`} block={block} top={percent(placed.top)} height={percent(placed.height)} timeZone={timeZone} chipStyle={chipStyle} overlaying={overlaying} date={day.date} onOpenEvent={onOpenEvent} />;
            })}
        </div>
      ))}
      {opened ? <AnchoredCard opened={opened} layout="week" renderCard={renderCard} /> : null}
    </div>
  );
}

function Block({ block, top, height, timeZone, chipStyle, overlaying, date, onOpenEvent }: { block: WeekBlock; top: string; height: string; timeZone: string; chipStyle: (event: GridEvent) => CSSProperties | undefined; overlaying: boolean; date: string; onOpenEvent: (opened: OpenedEvent | null) => void }) {
  if (block.kind === 'travel') {
    return (
      <span className={styles.block} style={{ top, height }} data-travel="true" data-calendar-travel={block.event.id}>
        <span className={styles.blockTitle}>{travelLabel(block.event.travelBufferMinutes ?? 0, t('calendar.travel'))}</span>
      </span>
    );
  }
  // 🔨 A block tall enough shows more of the booking: the time range and place, then the notes and the attendees (product owner, 2026-09-10). A short one keeps its one line.
  const detail = blockDetail(block.startMinute, block.endMinute);
  const event = block.event;
  const place = event.eventType === 'Online' ? event.meetingLink : event.eventType === 'Phone' ? event.callNumber : event.place;
  const range = event.endsAt ? `${clockOf(event.startsAt, timeZone)} – ${clockOf(event.endsAt, timeZone)}` : clockOf(event.startsAt, timeZone);
  return (
    <button
      type="button"
      className={styles.block}
      style={{ top, height, ...chipStyle(event) }}
      data-kind={chipKind(event.eventType)}
      data-owned={overlaying ? 'true' : 'false'}
      data-cancelled={event.cancelledAt ? 'true' : 'false'}
      data-calendar-chip={event.id}
      data-block-detail={String(detail)}
      onClick={() => onOpenEvent({ eventKey: event.id, date })}
      title={event.subject}
    >
      <span className={styles.blockTitle}>
        {detail === 0 ? chipLabel(event, timeZone, block.continuesBefore) : `${event.ownerLabel ? `${event.ownerLabel}: ` : ''}${event.subject}`}
        {block.continuesAfter ? ' →' : ''}
      </span>
      {detail >= 1 ? <span className={styles.blockLine} data-block-range="true">{range}</span> : null}
      {detail >= 1 && place ? <span className={styles.blockLine} data-block-place="true">{place}</span> : null}
      {detail >= 2 && event.notes ? <span className={styles.blockNotes} data-block-notes="true">{event.notes}</span> : null}
      {detail >= 2 && event.attendees && event.attendees.length > 0 ? (
        <span className={styles.blockAttendees} data-block-attendees="true">
          <AvatarRow attendees={event.attendees.map((a) => ({ person: { displayName: a.displayName, avatarColor: a.avatarColor }, attending: a.attending as Attending }))} />
        </span>
      ) : null}
    </button>
  );
}
